import React, { createContext } from 'react';
import { AxiosResponse } from 'axios';

import { Device, Loadable, Med } from '../types';
import { medicationService } from '../api/services/medicationService';
import { useDeviceCollection } from '../hooks';

interface MedicationContextType {
  device?: Device;
  meds?: Loadable<Med>;
  saveMed: (med: Med) => Promise<void>;
  removeMed: (medId: string) => Promise<void>;
}

const loadableMeds: Loadable<Med> = { loading: true };

const initialContext: MedicationContextType = {
  meds: loadableMeds,
  saveMed: () => Promise.resolve(),
  removeMed: () => Promise.resolve(),
};

const MedicationContext = createContext<MedicationContextType>(initialContext);

interface MedicationProviderProps {
  device?: Device;
  children: React.ReactNode;
}

const mapMeds = (data: Med[]): Med[] =>
  data.map((a) => ({ ...a, createdAt: new Date(a.createdAt) }));

function MedicationProvider({ device, children }: MedicationProviderProps) {
  const { items: meds, setItems: setMeds } = useDeviceCollection<Med>(
    device,
    medicationService.getMedications,
    { mapper: mapMeds },
  );

  const saveMed = (med: Med) => {
    if (!device) return Promise.reject('no device');

    const handleResponse = (resolve: Function, response: AxiosResponse<Med>, isNew: boolean) => {
      const medsData = meds.data || [];
      const update = { ...response.data, createdAt: new Date(response.data.createdAt) };
      const data = isNew
        ? [...medsData, update]
        : medsData.map((c) => (c.id === med.id ? update : c));
      setMeds({ loading: false, data });
      resolve();
    };

    const handleError = (reject: Function, err: unknown) => {
      setMeds({ loading: false, error: err as Error });
      reject(err);
    };

    return new Promise<void>((resolve, reject) => {
      if (med.id) {
        medicationService
          .updateMedication(device.id, med.id, med)
          .then((response) => handleResponse(resolve, response, false))
          .catch((err) => handleError(reject, err));
      } else {
        medicationService
          .createMedication(device.id, med)
          .then((response) => handleResponse(resolve, response, true))
          .catch((err) => handleError(reject, err));
      }
    });
  };

  const removeMed = async (medId: string) => {
    if (!device) return Promise.reject('no device');

    try {
      await medicationService.deleteMedication(device.id, medId);
      return setMeds({ loading: false, data: meds.data?.filter((c) => c.id !== medId) });
    } catch (err) {
      return setMeds({ loading: false, error: err as Error });
    }
  };

  return (
    <MedicationContext.Provider
      value={{
        device,
        meds,
        saveMed,
        removeMed,
      }}
    >
      {children}
    </MedicationContext.Provider>
  );
}

export { MedicationContext, MedicationProvider };
